// /app/frontend/src/pages/compliance/readiness/ReadinessDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, RefreshCw, Download, FileText, CheckCircle2,
  Clock, AlertTriangle, Building2, User, Mail, Calendar, Trash2, Ban
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../../components/ui/table';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../../../components/ui/accordion';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../../components/ui/alert-dialog';

import { readinessAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500' },
  sent: { label: 'Sent', color: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500' },
  completed: { label: 'Completed', color: 'bg-green-500' },
  expired: { label: 'Expired', color: 'bg-red-500' },
  voided: { label: 'Voided', color: 'bg-gray-500' }
};

const ANSWER_LABELS = {
  yes: 'Yes',
  partial: 'Partial',
  no: 'No',
  na: 'N/A'
};

const ANSWER_COLORS = {
  yes: 'bg-green-500',
  partial: 'bg-yellow-500',
  no: 'bg-red-500',
  na: 'bg-gray-400'
};

export default function ReadinessDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isComplianceReadOnly: isReadOnly } = useAuth();

  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);

  useEffect(() => {
    loadAssessment();
  }, [id]);

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const response = await readinessAPI.getById(id);
      setAssessment(response.data);
    } catch (error) {
      console.error('Error loading assessment:', error);
      toast.error('Failed to load assessment');
      navigate('/compliance/readiness');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      await readinessAPI.send(id);
      toast.success('Assessment sent');
      setShowSendDialog(false);
      loadAssessment();
    } catch (error) {
      console.error('Error sending assessment:', error);
      toast.error('Failed to send assessment');
    } finally {
      setSending(false);
    }
  };

  const handleSendReminder = async () => {
    try {
      await readinessAPI.sendReminder(id);
      toast.success('Reminder sent');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await readinessAPI.exportCsv(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `readiness_assessment_${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleDelete = async () => {
    try {
      await readinessAPI.delete(id);
      toast.success('Assessment deleted');
      navigate('/compliance/readiness');
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Failed to delete assessment');
    }
  };

  const handleVoid = async () => {
    try {
      await readinessAPI.void(id);
      toast.success('Assessment voided');
      setShowVoidDialog(false);
      loadAssessment();
    } catch (error) {
      console.error('Error voiding assessment:', error);
      toast.error('Failed to void assessment');
    }
  };

  if (loading || !assessment) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[assessment.status] || STATUS_CONFIG.draft;
  const isCompleted = assessment.status === 'completed';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="readiness-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/readiness')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{assessment.entity_name}</h1>
                <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>
              </div>
              <p className="text-muted-foreground">{assessment.client_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isReadOnly && assessment.status === 'draft' && (
              <Button onClick={() => setShowSendDialog(true)}>
                <Send className="h-4 w-4 mr-2" />
                Send Assessment
              </Button>
            )}
            {!isReadOnly && ['sent', 'in_progress'].includes(assessment.status) && (
              <Button variant="outline" onClick={handleSendReminder}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Send Reminder
              </Button>
            )}
            {isCompleted && (
              <Button variant="outline" onClick={handleExportCsv}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            )}
            {!isReadOnly && ['draft', 'sent', 'in_progress', 'expired'].includes(assessment.status) && (
              <Button variant="outline" className="text-destructive border-destructive hover:bg-destructive/10" onClick={() => setShowDeleteDialog(true)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            {!isReadOnly && assessment.status === 'completed' && (
              <Button variant="outline" onClick={() => setShowVoidDialog(true)}>
                <Ban className="h-4 w-4 mr-2" />
                Void
              </Button>
            )}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{assessment.assessment_type}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Jurisdiction</p>
                  <p className="font-medium">{assessment.jurisdiction_label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Recipient</p>
                  <p className="font-medium">{assessment.recipient_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Progress</p>
                  <p className="font-medium">{assessment.answered_count} / {assessment.question_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results (only for completed) */}
        {isCompleted && (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="sections">Section Scores</TabsTrigger>
              <TabsTrigger value="responses">All Responses</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Summary Narrative (spec Section 4.4) */}
              {assessment.summary_narrative && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {assessment.summary_narrative}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Score Gauge */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-8">
                    {/* Circular Gauge */}
                    <div className="relative w-40 h-40">
                      <svg className="w-40 h-40 transform -rotate-90">
                        <circle cx="80" cy="80" r="70" className="stroke-muted" strokeWidth="12" fill="none" />
                        <circle
                          cx="80" cy="80" r="70"
                          stroke={assessment.overall_rating_color || '#0ea5e9'}
                          strokeWidth="12"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 70}`}
                          strokeDashoffset={`${2 * Math.PI * 70 * (1 - (assessment.overall_score || 0) / 100)}`}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 0.5s' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-4xl font-bold">{assessment.overall_score?.toFixed(0)}%</span>
                        <span className="text-sm text-muted-foreground">{assessment.overall_rating_label}</span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <h3 className="text-xl font-semibold">Overall Readiness Score</h3>
                        <p className="text-muted-foreground">
                          Based on {assessment.question_count} questions across {assessment.section_count} areas
                        </p>
                      </div>

                      {/* Response Distribution */}
                      {assessment.response_distribution && (
                        <div className="grid grid-cols-4 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-500">
                              {assessment.response_distribution.yes?.count || 0}
                            </div>
                            <div className="text-sm text-muted-foreground">Yes</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-500">
                              {assessment.response_distribution.partial?.count || 0}
                            </div>
                            <div className="text-sm text-muted-foreground">Partial</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-500">
                              {assessment.response_distribution.no?.count || 0}
                            </div>
                            <div className="text-sm text-muted-foreground">No</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-400">
                              {assessment.response_distribution.na?.count || 0}
                            </div>
                            <div className="text-sm text-muted-foreground">N/A</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Priority Areas */}
              {assessment.priority_areas && assessment.priority_areas.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      Priority Areas
                    </CardTitle>
                    <CardDescription>Areas requiring the most attention based on assessment results</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {assessment.priority_areas.map((area, index) => (
                        <div key={area.section_id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-medium">{index + 1}. {area.section_title}</span>
                              <Badge variant="outline" style={{ color: area.rating_color }}>
                                {area.rating_label}
                              </Badge>
                            </div>
                            <span className="text-2xl font-bold" style={{ color: area.rating_color }}>
                              {area.score?.toFixed(0)}%
                            </span>
                          </div>
                          <Progress value={area.score} className="h-2 mb-3" />
                          {area.gaps && area.gaps.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <p className="text-sm text-muted-foreground">{area.gap_count} gaps identified:</p>
                              {area.gaps.slice(0, 3).map(gap => (
                                <div key={gap.question_id} className="flex items-start gap-2 text-sm">
                                  <Badge className={`${ANSWER_COLORS[gap.answer]} text-white text-xs`}>
                                    {ANSWER_LABELS[gap.answer]}
                                  </Badge>
                                  <span className="text-muted-foreground line-clamp-1">{gap.question_text}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="sections">
              <Card>
                <CardHeader>
                  <CardTitle>Section Scores</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {assessment.section_scores?.map(section => (
                      <div key={section.section_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{section.section_title}</span>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" style={{ color: section.rating_color }}>
                              {section.rating_label}
                            </Badge>
                            <span className="text-xl font-bold" style={{ color: section.rating_color }}>
                              {section.score?.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        <Progress value={section.score} className="h-2 mb-2" />
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="text-green-500">Yes: {section.yes_count}</span>
                          <span className="text-yellow-500">Partial: {section.partial_count}</span>
                          <span className="text-red-500">No: {section.no_count}</span>
                          <span className="text-gray-400">N/A: {section.na_count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="responses">
              <Card>
                <CardHeader>
                  <CardTitle>All Responses</CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="multiple" className="space-y-2">
                    {(() => {
                      // Group responses by section
                      const grouped = {};
                      (assessment.responses || []).forEach(r => {
                        const sid = r.section_id || 'other';
                        if (!grouped[sid]) {
                          grouped[sid] = {
                            title: r.section_title || 'Other',
                            responses: []
                          };
                        }
                        grouped[sid].responses.push(r);
                      });
                      
                      return Object.entries(grouped).map(([sectionId, section]) => (
                        <AccordionItem key={sectionId} value={sectionId} className="border rounded-lg px-4">
                          <AccordionTrigger className="hover:no-underline">
                            <span className="font-medium">{section.title}</span>
                            <span className="text-sm text-muted-foreground ml-2">
                              ({section.responses.length} questions)
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3 py-2">
                              {section.responses.map(response => (
                                <div key={response.question_id} className="border-b pb-3 last:border-0">
                                  <div className="flex items-start gap-3">
                                    <Badge className={`${ANSWER_COLORS[response.answer]} text-white mt-1`}>
                                      {ANSWER_LABELS[response.answer]}
                                    </Badge>
                                    <div className="flex-1">
                                      <p className="text-sm">{response.question_text}</p>
                                      {response.comment && (
                                        <p className="text-xs text-muted-foreground mt-1 italic">
                                          Comment: {response.comment}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ));
                    })()}
                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Info for non-completed */}
        {!isCompleted && (
          <Card>
            <CardHeader>
              <CardTitle>Assessment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Recipient Email</p>
                  <p className="font-medium">{assessment.recipient_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Recipient Role</p>
                  <p className="font-medium">{assessment.recipient_role || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Applicable Law</p>
                  <p className="font-medium">{assessment.jurisdiction_act}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expires</p>
                  <p className="font-medium">
                    {assessment.expires_at ? new Date(assessment.expires_at).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created By</p>
                  <p className="font-medium">{assessment.consultant_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created At</p>
                  <p className="font-medium">{new Date(assessment.created_at).toLocaleString()}</p>
                </div>
              </div>
              {assessment.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm mt-1 bg-muted p-3 rounded">{assessment.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Send Dialog */}
        <AlertDialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will send an email invitation to{' '}
                <strong>{assessment.recipient_name}</strong> at{' '}
                <strong>{assessment.recipient_email}</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSend} disabled={sending}>
                {sending ? 'Sending...' : 'Send'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                {assessment.status === 'in_progress'
                  ? 'The respondent has started this assessment. Deleting will discard their progress. This action cannot be undone.'
                  : 'This action cannot be undone. The assessment will be permanently deleted.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Void Dialog */}
        <AlertDialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Void Assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the assessment as voided and hide it from active views. The record will be kept for audit purposes. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleVoid}>
                Void Assessment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
